import React from 'react';
import PropTypes from 'prop-types';
import noop from 'lodash/noop';
import numeral from 'numeral';
import {
  Modal,
  Button,
  ListGroup,
  ListGroupItem,
  ProgressBar,
  Checkbox,
  FormGroup,
  FormControl,
} from 'react-bootstrap';
import FieldGroup from './FieldGroup';
import { dollarAmountValidation } from '../utils';

const escapeHtml = (s) =>
  String(s == null ? '' : s).replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]),
  );

const buildPrintHtml = (invoices, sum) => {
  const rows = invoices
    .map(
      ({ customerName, saleDate, contract, balanceDue }) => `
      <tr>
        <td>${escapeHtml(customerName)}</td>
        <td>${escapeHtml(contract)}</td>
        <td>${escapeHtml(saleDate)}</td>
        <td class="right">${numeral(balanceDue).format('$0,0.00')}</td>
      </tr>`,
    )
    .join('');

  return `<!DOCTYPE html>
<html>
  <head>
    <title>Payment Summary</title>
    <style>
      body { font-family: Arial, sans-serif; padding: 24px; color: #222; }
      h1 { margin: 0 0 4px; font-size: 20px; }
      .meta { color: #666; margin-bottom: 16px; font-size: 12px; }
      table { width: 100%; border-collapse: collapse; }
      th, td { padding: 8px 10px; border-bottom: 1px solid #ddd; text-align: left; font-size: 12px; }
      th { background: #f5f5f5; }
      .right { text-align: right; }
      tfoot td { font-weight: bold; border-top: 2px solid #333; border-bottom: none; }
      @media print { body { padding: 0; } }
    </style>
  </head>
  <body>
    <h1>Payment Summary</h1>
    <div class="meta">${invoices.length} invoices &middot; ${new Date().toLocaleString()}</div>
    <table>
      <thead>
        <tr><th>Customer</th><th>Contract</th><th>Sale Date</th><th class="right">Balance Due</th></tr>
      </thead>
      <tbody>${rows}</tbody>
      <tfoot>
        <tr><td colspan="3">Total</td><td class="right">${numeral(sum).format('$0,0.00')}</td></tr>
      </tfoot>
    </table>
    <script>
      window.addEventListener('load', function () {
        window.focus();
        window.print();
        window.addEventListener('afterprint', function () { window.close(); });
      });
    </script>
  </body>
</html>`;
};

const PaymentModal = ({
  onSubmit,
  onCancel,
  handleBounceFee,
  bounceFee = 0,
  handleCustomAmount,
  customAmount = 0,
  customAmountCheck,
  handleCustomAmountCheck,
  invoices = [],
  sum = 0,
  processing,
  customMessage,
  handleCustomMessageChange,
  ...props
}) => {
  const validCustomAmount = customAmountCheck ? !!dollarAmountValidation(customAmount) : true;
  const handlePrint = () => {
    const win = window.open('', '_blank', 'width=800,height=600');
    if (!win) return;
    win.document.write(buildPrintHtml(invoices, sum));
    win.document.close();
  };
  return (
    <Modal {...props}>
      <Modal.Header>
        <Modal.Title>Confirm Payment</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {processing && <ProgressBar striped active bsStyle="success" now={100} />}
        <h4>
          For {invoices.length} invoices totalling: <b>{numeral(sum).format('$0,0.00')}</b>
        </h4>
        <ListGroup style={{ maxHeight: 400, overflow: 'auto', textAlign: 'center' }}>
          {invoices.map(({ customerName, saleDate, contract, balanceDue }) => (
            <ListGroupItem key={contract}>
              {`${customerName} - ${contract} - ${numeral(balanceDue).format(
                '$0,0.00',
              )} - ${saleDate}`}
            </ListGroupItem>
          ))}
        </ListGroup>
        <div>
          <Checkbox
            type="checkbox"
            id="fee-checkbox"
            name="fee-checkbox"
            checked={!!bounceFee}
            onChange={(e) => handleBounceFee(e.target.checked)}
          >
            Apply $40 NSF (bounce) fee to this payment.
          </Checkbox>
          <Checkbox
            id="custom-fee-checkbox"
            name="custom-fee-checkbox"
            checked={customAmountCheck}
            onChange={(e) => handleCustomAmountCheck(e.target.checked)}
          >
            Apply additional amount to this payment.
          </Checkbox>
        </div>
        {customAmountCheck && (
          <form>
            <FieldGroup
              id="formControlsEmail"
              type="number"
              label=""
              placeholder="Additional amount"
              value={customAmount || ''}
              onChange={(e) => handleCustomAmount(e.target.value)}
              validationState={!validCustomAmount ? 'error' : null}
              help={!validCustomAmount ? 'Enter valid dollar amount' : null}
            />
            <FormGroup controlId="formControlsTextarea">
              <FormControl
                componentClass="textarea"
                placeholder="Comment (optional)..."
                value={customMessage}
                onChange={(e) => handleCustomMessageChange(e.target.value)}
              />
            </FormGroup>
          </form>
        )}
        <p>
          <i>
            If you do not wish to pay this amount, close this window and adjust your total. You will
            not be able to change the price after clicking 'Submit Payment'.
          </i>
        </p>
      </Modal.Body>

      <Modal.Footer>
        <Button bsStyle="info" onClick={handlePrint} disabled={invoices.length === 0}>
          Print PDF
        </Button>
        <Button onClick={onCancel}>Close</Button>
        <Button bsStyle="danger" onClick={onSubmit} disabled={!validCustomAmount}>
          Submit Payment
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

PaymentModal.propTypes = {
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  invoices: PropTypes.array,
  sum: PropTypes.number,
  processing: PropTypes.bool,
};

PaymentModal.defaultProps = {
  onSubmit: noop,
  onCancel: noop,
  invoices: [],
  sum: 0,
  processing: false,
};
export default PaymentModal;



// WEBPACK FOOTER //
// ./src/components/PaymentModal.js